import { useState, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  Drawer,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

// Recursive Mobile Submenu List Item
const MobileSubMenuItem = ({ sub, onSelect, onClose }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = sub.subcategories && sub.subcategories.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    } else {
      if (onSelect) onSelect(sub.slug);
      onClose();
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.2,
          px: 2,
          borderRadius: 2,
          mb: 0.5,
          justifyContent: "space-between",
        }}
      >
        <ListItemText
          primary={sub.title}
          primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 500 }}
        />
        {hasChildren &&
          (open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowLeftIcon fontSize="small" />
          ))}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pr: 2 }}>
            {sub.subcategories.map((child) => (
              <MobileSubMenuItem
                key={child.slug}
                sub={child}
                onSelect={onSelect}
                onClose={onClose}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// Recursive Desktop Submenu Item
const DesktopSubMenuItem = ({ sub, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef(null);
  const hasChildren = sub.subcategories && sub.subcategories.length > 0;

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHovered(false);
    }, 150);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(sub.slug);
    setHovered(false);
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: "relative", width: "100%" }}
    >
      <Button
        fullWidth
        onClick={handleClick}
        sx={{
          justifyContent: "space-between",
          py: 1,
          px: 1.5,
          borderRadius: 2,
          fontWeight: 500,
          fontSize: "0.88rem",
          color: hovered ? "#ef4056" : "text.primary",
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: "grey.100",
          },
        }}
      >
        {sub.title}
        {hasChildren && (
          <KeyboardArrowLeftIcon
            fontSize="small"
            sx={{
              color: hovered ? "#ef4056" : "grey.500",
              transition: "transform 0.2s ease",
              transform: hovered ? "translateX(-3px)" : "none",
            }}
          />
        )}
      </Button>

      {hovered && hasChildren && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            top: -4,
            right: "100%",
            mr: 1,
            bgcolor: "background.paper",
            borderRadius: 3,
            minWidth: 210,
            zIndex: 20,
            p: 1,
            border: "1px solid",
            borderColor: "grey.200",
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.08)",
            animation: "fadeIn 0.15s ease-out forwards",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateX(8px)" },
              "100%": { opacity: 1, transform: "translateX(0)" },
            },
          }}
        >
          {sub.subcategories.map((child) => (
            <DesktopSubMenuItem
              key={child.slug}
              sub={child}
              onSelect={handleSelect}
            />
          ))}
        </Paper>
      )}
    </Box>
  );
};

// Main Component
export default function CategoryItem({ cat, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const timeoutRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const hasChildren = cat.subcategories && cat.subcategories.length > 0;

  const handleSelect = (slug) => {
    if (!slug) return;

    if (onSelect) {
      onSelect(slug);
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setHovered(false);
    setDrawerOpen(false);
  };

  // Desktop Hover Handlers
  const handleMouseEnter = () => {
    if (isMobile) return;
    clearTimeout(timeoutRef.current);
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    timeoutRef.current = setTimeout(() => {
      setHovered(false);
    }, 150);
  };

  // Click Handler (Mobile vs Desktop)
  const handleClick = () => {
    if (isMobile && hasChildren) {
      setDrawerOpen(true);
    } else {
      
      handleSelect(cat.slug);
    }
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: "relative" }}
    >
      <Button
        disableRipple
        onClick={handleClick}
        sx={{
          fontSize: { xs: "0.85rem", md: "0.9rem" },
          fontWeight: 600,
          color: hovered || drawerOpen ? "#ef4056" : "text.secondary",
          px: { xs: 1.2, md: 1.5 },
          py: 0.8,
          borderRadius: "20px",
          bgcolor: isMobile ? "grey.100" : "transparent",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
          "&:hover": {
            color: "#ef4056",
            bgcolor: isMobile ? "grey.200" : "transparent",
          },
          // Desktop pill underline
          "&::after": !isMobile
            ? {
                content: '""',
                position: "absolute",
                bottom: 0,
                right: "15%",
                width: "70%",
                height: "2px",
                bgcolor: "#ef4056",
                transform: hovered ? "scaleX(1)" : "scaleX(0)",
                transition: "transform 0.2s ease-in-out",
                borderRadius: 2,
              }
            : {},
        }}
      >
        {cat.title}
        {isMobile && hasChildren && (
          <KeyboardArrowDownIcon
            sx={{ fontSize: "1rem", mr: 0.5, color: "grey.600" }}
          />
        )}
      </Button>

      {/* DESKTOP HOVER DROPDOWN */}
      {!isMobile && hovered && hasChildren && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            top: "100%",
            right: 0,
            mt: 0.5,
            bgcolor: "background.paper",
            borderRadius: 3,
            zIndex: 10,
            minWidth: 220,
            p: 1,
            border: "1px solid",
            borderColor: "grey.200",
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.08)",
            animation: "slideDown 0.15s ease-out forwards",
            "@keyframes slideDown": {
              "0%": { opacity: 0, transform: "translateY(-6px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: -10,
              left: 0,
              right: 0,
              height: 10,
            },
          }}
        >
          {cat.subcategories.map((sub) => (
            <DesktopSubMenuItem
              key={sub.slug}
              sub={sub}
              onSelect={handleSelect}
            />
          ))}
        </Paper>
      )}

      {/* MOBILE BOTTOM DRAWER (Triggered on Tap) */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "75vh",
              p: 2,
              direction: "rtl",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "1.05rem", fontWeight: 700 }}
            >
              {cat.title}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1.5 }} />

          <List disablePadding>
            {/* Option to select the main category itself */}
            <ListItemButton
              onClick={() => handleSelect(cat.slug)}
              sx={{
                py: 1.2,
                px: 2,
                borderRadius: 2,
                bgcolor: "grey.100",
                mb: 1.5,
              }}
            >
              <ListItemText
                primary={`مشاهده همه محصولات ${cat.title}`}
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#ef4056",
                }}
              />
            </ListItemButton>

            {/* Nested Subcategories */}
            {cat.subcategories?.map((sub) => (
              <MobileSubMenuItem
                key={sub.slug}
                sub={sub}
                onSelect={handleSelect}
                onClose={() => setDrawerOpen(false)}
              />
            ))}
          </List>
        </Drawer>
      )}
    </Box>
  );
}
